"use client";

import { useActionState, useRef } from "react";

import Form from "next/form";

import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import {
  CategoryProducts,
  getAccessories,
  ProductAccessoriesActionState,
} from "@/app/blu/shop/actions";
import { DEFAULT_PDP_CATEGORY } from "@/app/blu/shop/constants";
import { useProductSearchEvent } from "@/hooks/useProductSearchEvent";
import {
  GetConfiguratorQueryResult,
  GetQuizResultsQueryResult,
  RichText as RichTextType,
} from "@/sanity.types";

import { ConfiguratorAccessoriesFilter } from "./configuratorAccessoriesFilter.component";
import { ConfiguratorAccessoriesLoader } from "./configuratorAccessoriesLoader.component";
import { ConfiguratorAccessoriesNav } from "./configuratorAccessoriesNav.component";
import { ConfiguratorAccessoriesNotFound } from "./configuratorAccessoriesNotFound.component";
import { ConfiguratorAccessoriesProducts } from "./configuratorAccessoriesProducts.component";

export const ConfiguratorAccessoriesForm = ({
  products = [],
  title,
  titleClassName,
  subtitle,
  accessories,
  basketItems,
  categoryCallout,
  selectedCategory = DEFAULT_PDP_CATEGORY,
  kit,
  legalDisclosure,
}: ConfiguratorAccessoriesProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const initialResponse: ProductAccessoriesActionState = {
    products: products,
    search: "",
    sort: "recommended",
    categoryCallout,
    selectedCategory,
  };
  const [response, getAccessoriesAction, pending] = useActionState<
    ProductAccessoriesActionState,
    FormData
  >(getAccessories, initialResponse);
  useProductSearchEvent(response.search, response.products);
  const renderAccessories = () => {
    if (pending) return <ConfiguratorAccessoriesLoader />;
    if (!response.products?.length) {
      return <ConfiguratorAccessoriesNotFound response={response} />;
    }
    return (
      <ConfiguratorAccessoriesProducts
        products={response?.products}
        basketItems={basketItems}
        pending={pending}
        kit={kit}
      />
    );
  };
  return (
    <section
      data-testid="configurator-accessories"
      className="flex-1 overflow-hidden">
      <div className="container flex flex-col gap-6 px-4">
        <Text
          variant="h5"
          data-testid="configurator-accessories-title"
          className={titleClassName}>
          {title}
        </Text>
        <RichText>{subtitle}</RichText>
        <Form
          data-testid="configurator-accessories-form"
          action={getAccessoriesAction}
          ref={formRef}>
          <div
            data-testid="configurator-accessories-filter"
            className="flex flex-col gap-8">
            <ConfiguratorAccessoriesFilter
              formRef={formRef}
              response={response}
              pending={pending}
            />
            <ConfiguratorAccessoriesNav
              formRef={formRef}
              response={response}
              accessories={accessories}
            />
          </div>
        </Form>
        <div className="flex min-h-125 w-full flex-1 flex-col">
          <input id="kit-id" type="hidden" name="kitId" defaultValue={kit.id} />
          {renderAccessories()}
        </div>
        {/* Legal disclosure shown on the quiz results page in mobile view. It is hidden in desktop view, because it is already shown in the configurator summary component.*/}
        {legalDisclosure ? (
          <div className="lg:hidden">
            <RichText data-testid="configurator-accessories-legal-disclosure">
              {legalDisclosure}
            </RichText>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export type ConfiguratorAccessoriesProps = {
  products?: CategoryProducts | null | undefined;
  accessories?: NonNullable<ConfiguratorPage>["accessories"];
  title?: string;
  subtitle?: RichTextType | null;
  titleClassName?: string;
  categoryCallout?: RichTextType | null;
  selectedCategory: string;
  basketItems?: Record<string, number>;
  kit: NonNullable<ConfiguratorPage>["product"];
  legalDisclosure?: NonNullable<GetQuizResultsQueryResult>["legalDisclosure"];
};

type ConfiguratorPage = GetConfiguratorQueryResult | GetQuizResultsQueryResult;
